import React from "react";
import { X, Plus } from "lucide-react";

export const TextInput = ({ 
  label, 
  value, 
  onChange, 
  disabled = false, 
  placeholder = "",
  className = "" 
}) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500 text-sm"
    />
  </div>
);

export const TextArea = ({ 
  label, 
  value, 
  onChange, 
  rows = 4, 
  placeholder = "",
  className = "" 
}) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    />
  </div>
);

// Select dropdown
export const SelectField = ({ 
  label, 
  value, 
  onChange, 
  options = [], 
  placeholder = "Select...",
  disabled = false,
  className = "" 
}) => (
  <div className={`space-y-1.5 ${className}`}>
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 text-sm"
    >
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt.value || opt} value={opt.value || opt}>
          {opt.label || opt}
        </option>
      ))}
    </select>
  </div>
);

export const Checkbox = ({ 
  label, 
  checked = false, 
  onChange, 
  disabled = false,
  className = "" 
}) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
    />
    <label className="text-sm font-medium text-gray-700 cursor-pointer select-none">
      {label}
    </label>
  </div>
);

export const FormSection = ({ title, children, className = "" }) => (
  <div className={`space-y-3 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-900 border-b border-gray-200 pb-2">
      {title}
    </h3>
    {children}
  </div>
);

export const FieldRow = ({ children, className = "" }) => (
  <div className={`flex gap-3 ${className}`}>
    {children}
  </div>
);

export const PitchInput = ({ pitch, onChange, onRemove }) => (
  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
    <input
      type="text"
      value={pitch.name}
      onChange={(e) => onChange({ ...pitch, name: e.target.value })}
      placeholder="Pitch type (e.g., FB, SL)"
      className="flex-1 px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
    />
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={pitch.velocityMin || ""}
        onChange={(e) => onChange({ ...pitch, velocityMin: e.target.value })}
        placeholder="Min"
        className="w-14 px-2 py-1.5 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <span className="text-gray-400 text-sm">-</span>
      <input
        type="text"
        value={pitch.velocityMax || ""}
        onChange={(e) => onChange({ ...pitch, velocityMax: e.target.value })}
        placeholder="Max"
        className="w-14 px-2 py-1.5 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <span className="text-gray-500 text-xs">mph</span>
    </div>
    <button
      type="button"
      onClick={onRemove}
      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
    >
      <X size={16} />
    </button>
  </div>
);

export const PitchArsenal = ({ pitches = [], onChange }) => {
  const addPitch = () => {
    onChange([...pitches, { id: Date.now(), name: "", velocityMin: "", velocityMax: "" }]);
  };

  const updatePitch = (index, updatedPitch) => {
    const newPitches = [...pitches];
    newPitches[index] = updatedPitch;
    onChange(newPitches);
  };

  const removePitch = (index) => {
    onChange(pitches.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">Pitch Arsenal</label>
        <button
          type="button"
          onClick={addPitch}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
        >
          <Plus size={14} />
          Add Pitch
        </button>
      </div>
      
      {pitches.length === 0 ? (
        <div className="text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-sm text-gray-500">No pitches added yet</p>
          <button
            type="button"
            onClick={addPitch}
            className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Add first pitch
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {pitches.map((pitch, index) => (
            <PitchInput
              key={pitch.id}
              pitch={pitch}
              onChange={(updated) => updatePitch(index, updated)}
              onRemove={() => removePitch(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const PitchSelectionGroup = ({ 
  label, 
  valueRHH, 
  valueLHH, 
  onChangeRHH, 
  onChangeLHH, 
  pitchOptions = [] 
}) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <span className="text-xs text-gray-500">vs RHH</span>
        <select
          value={valueRHH || ""}
          onChange={(e) => onChangeRHH(e.target.value)}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Select...</option>
          {pitchOptions.map((pitch) => (
            <option key={`rhh-${pitch}`} value={pitch}>
              {pitch}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <span className="text-xs text-gray-500">vs LHH</span>
        <select
          value={valueLHH || ""}
          onChange={(e) => onChangeLHH(e.target.value)}
          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Select...</option>
          {pitchOptions.map((pitch) => (
            <option key={`lhh-${pitch}`} value={pitch}>
              {pitch}
            </option>
          ))}
        </select>
      </div>
    </div>
  </div>
);

// 3x3 Strike Zone Grid selector
export const StrikeZoneGrid = ({ 
  label, 
  selectedZones = [], 
  onChange,
  className = "" 
}) => {
  const zones = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  const toggleZone = (zone) => {
    if (selectedZones.includes(zone)) {
      onChange(selectedZones.filter(z => z !== zone));
    } else {
      onChange([...selectedZones, zone]);
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <label className="block text-sm font-medium text-gray-700 text-center">{label}</label>}
      <div className="inline-flex flex-col items-center w-full">
        {/* Strike zone grid */}
        <div className="grid grid-cols-3 gap-0.5 p-1 bg-gray-200 rounded-lg">
          {zones.map((zone) => {
            const isSelected = selectedZones.includes(zone);
            return (
              <button
                key={zone}
                type="button"
                onClick={() => toggleZone(zone)}
                className={`w-10 h-10 flex items-center justify-center text-xs font-semibold rounded transition-all ${
                  isSelected
                    ? 'bg-blue-500 text-white shadow-sm'
                    : 'bg-white text-gray-500 hover:bg-gray-100'
                }`}
              >
                {zone}
              </button>
            );
          })}
        </div>
        <svg width="50" height="16" viewBox="0 0 50 16" className="mt-1">
          <path 
            d="M5 0 L45 0 L50 8 L25 16 L0 8 Z" 
            fill="#d1d5db" 
          />
        </svg>
      </div>
      {selectedZones.length > 0 && (
        <p className="text-xs text-gray-500">
          Selected: {selectedZones.sort((a, b) => a - b).join(', ')}
        </p>
      )}
    </div>
  );
};

const HITTER_TYPES = [
  { value: "pull", label: "Pull" },
  { value: "all_fields", label: "All Fields" },
  { value: "oppo", label: "Oppo" },
];

export const HitterTypeSelect = ({ 
  label,
  value = "",
  onChange,
  className = ""
}) => (
  <div className={`space-y-1.5 ${className}`}>
    {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
    >
      <option value="">Select type...</option>
      {HITTER_TYPES.map((type) => (
        <option key={type.value} value={type.value}>
          {type.label}
        </option>
      ))}
    </select>
  </div>
);

